/**
 * ContentSettingsStep
 *
 * Fourth step of the add content flow.
 * Displays 5 content settings: Schedule, Early Access, Link Content,
 * Allow Comments, Show Earned Coins — 4 in the comment-reply flow, where the
 * Schedule row is dropped (`hideSchedule`).
 * Matches Flutter's ContentSettingsScreen from content_settings_screen.dart.
 */

import React, { useCallback } from 'react';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useRegisterStepNav } from '@/presentation/components/common/stepper';
import { Divider } from '@/presentation/components/common/Divider';
import { useContentSettings, type ContentSettingsResult } from '../../hooks/useContentSettings';
import { ContentSettingItem, ContentSettingVariant } from './contentSettingsStep/ContentSettingItem';
import { LinkContentSettingItem } from './contentSettingsStep/LinkContentSettingItem';

interface ContentSettingsStepProps {
  mediaId: string;
  onNext: (result: ContentSettingsResult) => void;
  onBack: () => void;
  screenKey?: string;
  /**
   * Comment-reply publish flow — Flutter returns an empty `Container()` for the
   * schedule widget when `contentReplyCommentInfo != null`,
   * `content_settings_screen.dart:565-568`. With the row gone the schedule sheet
   * is unreachable, so `schedule` stays `null` and `isDraft` stays `false` —
   * exactly as on Flutter. No extra suppression logic is needed.
   */
  hideSchedule?: boolean;
}

export const ContentSettingsStep: React.FC<ContentSettingsStepProps> = ({
  mediaId,
  onNext,
  onBack,
  screenKey,
  hideSchedule,
}) => {
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const {
    allowEarlyAccess,
    allowComments,
    showCoins,
    linkedMedia,
    scheduleStatusText,
    handleScheduleClick,
    handleLinkContentClick,
    handleUnlinkContent,
    handleAllowEarlyAccessToggle,
    handleAllowCommentsToggle,
    handleShowCoinsToggle,
    buildResult,
  } = useContentSettings({ mediaId, screenKey });

  const handleNext = useCallback(() => {
    const result = buildResult();
    onNext(result);
  }, [buildResult, onNext]);

  useRegisterStepNav(
    { onNext: handleNext, onPrevious: onBack },
    [handleNext, onBack],
  );

  return (
    <div
      className="scrollbar-hide"
      style={{
        height: '100%',
        overflowY: 'auto',
        padding: `0 ${responsive.spacing.xl}px`,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
        }}
      >
        {/* Content section */}
        <div style={{ paddingTop: responsive.spacing.xl }}>
          {/* 1. Schedule — absent for the comment-reply flow (Flutter renders an
              empty `Container()`, content_settings_screen.dart:565-568). Its
              trailing divider goes with it so the list does not open on an
              orphan rule. */}
          {!hideSchedule && (
            <>
              <ContentSettingItem
                variant={ContentSettingVariant.tappable}
                qaId="content-setting-schedule"
                iconPath="/assets/images/timer.png"
                label={l10n.publish.scheduleLabel}
                description={l10n.publish.scheduleDescription}
                statusText={scheduleStatusText}
                onClick={handleScheduleClick}
              />

              <Divider />
            </>
          )}

          {/* 2. Allow Early Access */}
          <ContentSettingItem
            variant={ContentSettingVariant.toggle}
            qaId="content-setting-early-access"
            iconPath="/assets/images/early_access.png"
            label={l10n.publish.allowEarlyAccessLabel}
            description={l10n.publish.allowEarlyAccessDescription}
            value={allowEarlyAccess}
            onChange={handleAllowEarlyAccessToggle}
          />

          <Divider />

          {/* 3. Link Content */}
          <LinkContentSettingItem
            iconPath="/assets/images/link.png"
            description={l10n.publish.linkContentDescription}
            linkedMedia={linkedMedia}
            onLinkClick={handleLinkContentClick}
            onUnlinkClick={handleUnlinkContent}
          />

          <Divider />

          {/* 4. Allow Comments — no description (matches Flutter) */}
          <ContentSettingItem
            variant={ContentSettingVariant.toggle}
            qaId="content-setting-comments"
            iconPath="/assets/images/comment_lines.png"
            label={l10n.publish.allowCommentsLabel}
            value={allowComments}
            onChange={handleAllowCommentsToggle}
          />

          <Divider />

          {/* 5. Show Earned Coins */}
          <ContentSettingItem
            variant={ContentSettingVariant.toggle}
            qaId="content-setting-show-coins"
            iconPath="/assets/images/coins.png"
            label={l10n.publish.showEarnedCoinsLabel}
            description={l10n.publish.showEarnedCoinsDescription}
            value={showCoins}
            onChange={handleShowCoinsToggle}
          />
        </div>

      </div>
    </div>
  );
};
